import * as React from 'react'
import Layout from '../layout/default'
import Link from '../components/utils/Link'
import Columns from '../components/utils/Columns'
import VerticalSpace from '../components/utils/VerticalSpace'
import Video from '../components/utils/Video'
import { Super, Footnotes } from '../components/utils/Footnotes'

// Assets
import Plugin from '../videos/plugin.mp4'
import Napkin from '../videos/napkin.mp4'
import Guts from '../videos/guts.mp4'
import Wireframes from '../images/wireframes.svg'
import Schematic from '../images/schematic.svg'

const IndexPage = () => {
	return (
		<Layout
			pageTitle={'Scribbling Interfaces'}
			description={
				'Exploring how machine learning could enhance design tools and workflows'
			}
			video={Plugin}
			videocaption={
				'An early prototype of the machine learning-powered plugin. Button, Text and Image placeholders are linked to Figma components and translated into higher fidelities.'
			}
		>
			<h3>About</h3>
			<p>
				Inspired by Airbnb's{' '}
				<Link href="//https://airbnb.design/sketching-interfaces/">
					Sketching Interfaces
				</Link>{' '}
				and with a personal interest in design tools, I seized Umeå
				Institute of Design's 2-week Machine Learning course to explore
				how machine learning could enhance design tools and workflows.
			</p>
			<p>
				<strong>
					The resultis a Figma plugin prototype that turns hand-drawn
					low-fidelity wireframes into higher-fidelity mockups
				</strong>{' '}
				. Instead of generating{' '}
				<mark>arbitrary and opinionated visual elements</mark>, the
				plugin links the output to an existing design system (Figma
				components), allowing for quick iterations with a coherent
				design language.{' '}
			</p>

			<p>
				For the prototype, I've trained a object-recognition model using{' '}
				<Link href="https://pjreddie.com/darknet/yolo/">YOLOv5</Link>{' '}
				and implemented it using{' '}
				<Link href="https://www.tensorflow.org/js">TensorflowJS</Link>.
				The plugin is currently not published, as I haven't found a good
				way to host or ship the trained model with the plugin.
			</p>
			<h3>Approach</h3>
			<Columns>
				<p>
					In order to train a custom object-recognition model , I've
					created a dataset of 500 hand-drawn wireframes, containing a
					variation of up to three different elements (images, buttons
					and text placeholders).
					<Super n={1} />
				</p>
				<figure>
					<img src={Wireframes} />
					<figcaption>
						The wireframes were drawn both digitally and analog
					</figcaption>
				</figure>
			</Columns>
			<p>
				With only one person contributing to the dataset, the model was
				very tailored to my brush strokes and turned out to be less
				accurate if someone else was sketching. I've tried to compensate
				for this by augmenting
				<Super n={2} /> the wireframes, netting me around 1500 images
				and increasing the overall robustness a little bit.
			</p>
			<h4>Putting things together</h4>
			<p>
				After training the model for several generations
				<Super n={3} /> and running a few predicitons on a local
				machine, I've wrapped the model in a small web app so friends
				and colleagues could interact with the model.
			</p>
			<VerticalSpace />
			<Video src={Napkin}>
				I've used a napkin as analogy and expression of turning rough
				ideas into higher fidelities.
			</Video>
			<VerticalSpace />
			<p>
				All in all, this project was quite a 'learn-as-you-go'
				experience and was only made possible by excellent mentors and
				resources
				<Super n={4} /> by the ML community.
			</p>
			<h3>How does it work?</h3>
			<p>
				When infering a YOLO object-recognition model, the model is 1.)
				fed with a source image, 2.) the model then does some inference
				magic and 3.) eventually returns the image along with{' '}
				<mark>bounding boxes of every predicted element</mark>.
			</p>
			<VerticalSpace />
			<figure>
				<img src={Schematic} />
				<figcaption>
					A high-level overview of the original input, the segmented
					output with its bounding boxes and the synthesized mockups.
				</figcaption>
			</figure>
			<VerticalSpace />
			<p>
				These bounding boxes provide us with the width, height and x/y
				position of every detected elements — or in other words, a{' '}
				<mark>blueprint we can use to re-draw the image</mark>. All we
				have to do is substitute each bounding box with its respective
				detected element et voilà, we magically increased the fidelity
				of our wireframe.
			</p>
			<VerticalSpace />
			<Video src={Guts}>
				Visualizing the bounding boxes predicted by the ML model.
			</Video>
			<VerticalSpace />
			<p>
				In the initial napkin version, the translation of scribbles to
				wireframes was very naïve because it translates the bounding
				boxes 1:1, without paying attention to ex. alignment or layout.
				While this can be improved by aligning items in near proximity
				(as seen in the Figma plugin), there are certainly more
				sophisticated solutions which ex. could utilize Figma's built-in
				layout features. Certainly something to work on for a next
				version.
			</p>
			<h3>Context</h3>
			<p>
				This project was part and deliverable of Umeå Institute of
				Design's <mark>Experience Prototyping course</mark> and{' '}
				<mark>2-week Machine Learning module</mark>, led and mentored by
				the excellent{' '}
				<Link href="https://ciid.dk/education/visiting-faculty/full-faculty-listing/jen-sykes/">
					Jen Skyes
				</Link>{' '}
				and{' '}
				<Link href="https://www.andreasrefsgaard.dk/">
					Andreas Refsgaard
				</Link>
				.
			</p>
			<Footnotes>
				<>
					For the sake of keeping this page concise, I'm not diving
					into technical details or rationales. YOLOv4 was choosen due
					to its relative ease of use and because similar work used it
					and vouched for it, see [4].
				</>
				<>
					In this context, augmentation refers to creating alterations
					of the source image, by ex. skewing and distorting it, in
					order to increase the size of the dataset.
				</>
				<>
					A generation, or more accurately epoch, refers to one
					training cycle of a machine learning algorithm. Put simply,
					more epochs increase the precision of the algorithm because
					the algorithm has more time to learn.
				</>
				<>
					i.a.
					<Link href="https://repositorio-aberto.up.pt/bitstream/10216/122189/2/351046.pdf">
						Live Web Prototypes from Hand-Drawn Mockups
					</Link>{' '}
					(2019)
				</>
			</Footnotes>
		</Layout>
	)
}

export default IndexPage
